import { useEffect, useMemo, useState } from "react";
import type { BboxPct, ExpenseFieldName } from "../lib/expenseCellBbox";
import { expenseCellBboxPct } from "../lib/expenseCellBbox";
import { spatialCellBboxPct, type ExpenseSpatial } from "../lib/expenseSpatial";
import { formatTimeInput } from "../lib/timeFormat";
import { formatDateInput } from "../lib/dateFormat";
import { expenseIssues, inheritableDate, canInheritDate } from "../lib/expensePlausibility";
import { PlausibilityBadge } from "./PlausibilityNav";

/** Diäten-Zeitfelder — bekommen die Stundenlisten-Autoformat-Logik
 *  ("8" → "08:00", "830" → "08:30") auf blur. */
const EXPENSE_TIME_FIELDS = new Set<ExpenseFieldName>(["abfahrt", "rueckkehr"]);

/**
 * Diäten-Pendant zum Stundenlisten-CompareView: pro (Entry-Zeile, Feld) den
 * PDF-Ausschnitt NEBEN dem editierbaren OCR-Wert.
 *
 * Unterschied zur Stundenliste: dort liefert cell_dispatch persistierte
 * per-Zellen-Crops (bboxes.json + PNG) vom Server. Die Diäten-Pipeline hat
 * das (noch) nicht — die Crops entstehen hier CLIENT-seitig aus der einen
 * Preview-Bitmap via CSS-Ausschnitt (overflow:hidden + absolut positioniertes
 * Bild). Geometrie: meta.spatial (homography-projiziert, pixelgenau) wenn
 * vorhanden, sonst die statische Heuristik aus expenseCellBbox.ts.
 *
 * Human Quality Gate (glassbox-pipeline-pattern.md §7b): Das Urteil über die
 * Markierungs-Qualität wird in der ÜBERSICHT gefällt (nur dort ist die
 * Geometrie am ganzen PDF beurteilbar) — diese View ist erst erreichbar,
 * wenn der User dort freigegeben hat. Hier bleibt nur der „Sperren“-Ausweg,
 * falls beim Detail-Vergleich doch ein Geometrie-Fehler auffällt.
 */

export interface HighlightReview {
  status: "approved" | "blocked";
  by_name?: string;
  at?: string;
  note?: string | null;
  context?: { basis?: string; inliers?: number | null } | null;
}

type EntryCell = { value: string; confidence?: number } | string | undefined;
type Entry = Partial<Record<ExpenseFieldName, EntryCell>>;

function cellVal(c: EntryCell): string {
  if (!c) return "";
  if (typeof c === "string") return c;
  return c.value ?? "";
}

function cellConf(c: EntryCell): number | null {
  if (!c || typeof c === "string") return null;
  return typeof c.confidence === "number" ? c.confidence : null;
}

/** Innerer Crop-Renderer: zeigt Region `b` der Preview, optional mit
 *  AMBER-Highlight (Server-Stil der Stundenlisten-Crops: 30% Tint + 3px
 *  Rahmen #f59e0b) über der Original-Bbox. */
function CropRegion({ previewUrl, region, highlight, height, imgAspect }: {
  previewUrl: string;
  region: BboxPct;
  highlight: BboxPct | null;
  height: number;
  imgAspect: number;
}) {
  const cellAspect = (region.widthPct * imgAspect) / region.heightPct;
  return (
    <div style={{ position: "relative", height, aspectRatio: `${cellAspect}`, overflow: "hidden" }}>
      <img
        src={previewUrl}
        alt=""
        draggable={false}
        loading="lazy"
        style={{
          position: "absolute",
          width: `${10000 / region.widthPct}%`,
          left: `${-(region.leftPct * 100) / region.widthPct}%`,
          top: `${-(region.topPct * 100) / region.heightPct}%`,
          height: "auto", maxWidth: "none",
        }}
      />
      {highlight && (
        <div style={{
          position: "absolute",
          left: `${((highlight.leftPct - region.leftPct) / region.widthPct) * 100}%`,
          top: `${((highlight.topPct - region.topPct) / region.heightPct) * 100}%`,
          width: `${(highlight.widthPct / region.widthPct) * 100}%`,
          height: `${(highlight.heightPct / region.heightPct) * 100}%`,
          background: "rgba(245, 158, 11, 0.30)",
          border: "3px solid #f59e0b",
          boxSizing: "border-box",
          pointerEvents: "none",
        }} />
      )}
    </div>
  );
}

/** CSS-Crop in Stundenlisten-Maß: 38px hoch (= .ts-compare-img), Breite aus
 *  dem Bbox-Seitenverhältnis. Klick = Zoom als ZENTRIERTES Lightbox-Overlay:
 *  bei breiten Feldern (Kunde ~26% Seitenbreite) passt der erweiterte
 *  Ausschnitt nie in die Tabellenzelle — inline gekappt war das Highlight
 *  abgeschnitten (Thomas 2026-06-10). Das Overlay zeigt IMMER den ganzen
 *  Ausschnitt, markierte Zelle vollständig + Nachbar-Kontext, Höhe so
 *  gewählt, dass die Breite in den Viewport passt. */
function CellCrop({ previewUrl, bbox, imgAspect, maxW, zoomed, onToggleZoom }: {
  previewUrl: string;
  bbox: BboxPct;
  imgAspect: number; // naturalWidth / naturalHeight der Preview
  maxW: number;
  zoomed: boolean;
  onToggleZoom: () => void;
}) {
  if (bbox.widthPct <= 0 || bbox.heightPct <= 0) return null;
  const thumbAspect = (bbox.widthPct * imgAspect) / bbox.heightPct;

  // Zoom-Region: ±60% Spaltenbreite, ±0.8 Zeilenhöhe Nachbar-Kontext —
  // hart an die Bildränder geklemmt.
  const zl = Math.max(0, bbox.leftPct - bbox.widthPct * 0.6);
  const zt = Math.max(0, bbox.topPct - bbox.heightPct * 0.8);
  const zoomRegion: BboxPct = {
    leftPct: zl,
    topPct: zt,
    widthPct: Math.min(100 - zl, bbox.widthPct * 2.2),
    heightPct: Math.min(100 - zt, bbox.heightPct * 2.6),
  };
  const zoomAspect = (zoomRegion.widthPct * imgAspect) / zoomRegion.heightPct;
  // Höhe so wählen, dass die VOLLE Breite in den Viewport passt (nie kappen).
  const vw = typeof window !== "undefined" ? window.innerWidth : 1400;
  const zoomH = Math.max(60, Math.min(200, Math.floor((vw * 0.86) / zoomAspect)));

  return (
    <>
      <div
        onClick={(e) => { e.stopPropagation(); onToggleZoom(); }}
        title={zoomed ? "klick = schließen" : "klick = vergrößern (mit Nachbar-Kontext)"}
        style={{
          overflow: "hidden", height: 38, width: Math.min(maxW, 38 * thumbAspect),
          border: "1px solid #e5e7eb", borderRadius: 3, background: "#fafafa",
          flexShrink: 0, cursor: "zoom-in",
        }}
      >
        <CropRegion previewUrl={previewUrl} region={bbox} highlight={null} height={38} imgAspect={imgAspect} />
      </div>
      {zoomed && (
        <div
          onClick={(e) => { e.stopPropagation(); onToggleZoom(); }}
          title="klick = schließen"
          style={{
            position: "fixed", left: "50%", top: "50%",
            transform: "translate(-50%, -50%)", zIndex: 60,
            background: "#fff", padding: 8, borderRadius: 6,
            border: "1px solid #d1d5db",
            boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
            cursor: "zoom-out",
          }}
        >
          <CropRegion previewUrl={previewUrl} region={zoomRegion} highlight={bbox} height={zoomH} imgAspect={imgAspect} />
        </div>
      )}
    </>
  );
}

/** Fallback-Spaltenbreiten, solange das Preview-Seitenverhältnis noch lädt.
 *  Sobald imgAspect da ist, bestimmt die ECHTE Crop-Geometrie die Breite
 *  (das Highlight gibt an, wie viel Platz der Inhalt braucht — nichts wird
 *  abgeschnitten). */
const FIELD_WIDTH_FALLBACK: Record<string, number> = {
  tag: 80, datum: 170, kz_nr: 230, kunde: 500, zm_abzug: 110,
  m_verr: 110, ort: 210, abfahrt: 110, rueckkehr: 110,
};

interface Props {
  previewUrl: string;
  entries: Entry[];
  /** OCR-Originalwerte (Snapshot beim Laden) — zeigt "OCR: x" bei Abweichung. */
  originals: Entry[];
  fields: ReadonlyArray<{ key: ExpenseFieldName; label: string; placeholder: string }>;
  spatial: ExpenseSpatial | null;
  review: HighlightReview | null;
  /** Jahr der Karte ("2026") — für die Datums-Vervollständigung "16.3" → 16.03.2026. */
  cardYear?: string | null;
  busy: boolean;
  onVerdict: (status: "approved" | "blocked") => void;
  setCell: (rowIdx: number, key: ExpenseFieldName, value: string) => void;
  /* Karte 592: string statt ExpenseFieldName — der Hover-State ist seitenweit
     geteilt und traegt beim Fahrtenbuch eigene Feld-Keys. Diese View selbst
     bleibt Diaeten-only (Compare-Modus). */
  activeCell: { row: number; field: string } | null;
  onCellHover: (c: { row: number; field: string } | null) => void;
}

export function ExpenseCompareView({
  previewUrl, entries, originals, fields, spatial, review, cardYear, busy,
  onVerdict, setCell, activeCell, onCellHover,
}: Props) {
  // Preview-Naturalgröße einmal laden — die Crops brauchen das Seitenverhältnis.
  const [imgAspect, setImgAspect] = useState<number | null>(null);
  useEffect(() => {
    setImgAspect(null);
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) setImgAspect(img.naturalWidth / img.naturalHeight);
    };
    img.src = previewUrl;
    return () => { img.onload = null; };
  }, [previewUrl]);

  // Single-Zoom wie bei der Stundenliste: nur EINE Zelle gleichzeitig gezoomt,
  // Klick außerhalb schließt.
  const [zoomedCell, setZoomedCell] = useState<{ row: number; field: ExpenseFieldName } | null>(null);
  useEffect(() => {
    if (!zoomedCell) return;
    const onDocClick = () => setZoomedCell(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [zoomedCell]);

  // Spaltenbreite = natürliche Crop-Breite bei 38px Höhe (aus der echten
  // Bbox-Geometrie) — der volle Zellinhalt ist sichtbar, nichts abgeschnitten.
  const colWidths = useMemo(() => {
    const out: Record<string, number> = {};
    for (const f of fields) {
      if (imgAspect === null) {
        out[f.key] = FIELD_WIDTH_FALLBACK[f.key] ?? 160;
        continue;
      }
      const b = spatial ? spatialCellBboxPct(spatial, 0, f.key) : expenseCellBboxPct(0, f.key);
      if (!b || b.heightPct <= 0) {
        out[f.key] = FIELD_WIDTH_FALLBACK[f.key] ?? 160;
        continue;
      }
      const aspect = (b.widthPct * imgAspect) / b.heightPct;
      out[f.key] = Math.min(560, Math.max(72, Math.round(38 * aspect) + 12));
    }
    return out;
  }, [imgAspect, spatial, fields]);

  const approved = review?.status === "approved";
  const basis = spatial ? "pixelgenau (Template-Homography)" : "Heuristik (statisches Raster)";
  // Plausibilität (wie Stundenliste): ungültige Autocompletion-Felder rot.
  const plausIssues = expenseIssues(entries as any, cardYear);

  // Defensive: Diese View ist nur bei freigegebener Markierung erreichbar
  // (Toggle ist gesperrt). Falls das Verdict zwischenzeitlich wegfällt
  // (z.B. Re-OCR), nicht still falsche Crops zeigen.
  if (!approved) {
    return (
      <div style={{
        background: "#fffbeb", border: "1px solid #f59e0b", borderLeft: "4px solid #f59e0b",
        padding: 14, borderRadius: 4, fontSize: 13, color: "#92400e",
      }}>
        <strong>Vergleichen nicht freigegeben</strong>
        <div style={{ marginTop: 4 }}>
          Bitte in der Übersicht die Markierung prüfen und freigeben
          (Human Quality Gate).
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{
        fontSize: 12, color: "#166534", background: "#f0fdf4",
        border: "1px solid #bbf7d0", borderRadius: 4, padding: "6px 10px",
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <span>
          ✓ Vergleichen freigegeben durch {review?.by_name ?? "User"}
          {review?.at ? ` am ${new Date(review.at).toLocaleString("de-AT")}` : ""} · {basis}
        </span>
        <button type="button" className="link" disabled={busy}
                title="Markierungen passen doch nicht — Modus sperren (wird gespeichert, zurück zur Übersicht)"
                onClick={() => onVerdict("blocked")}>
          Sperren
        </button>
      </div>
      <div className="exp-compare-scroll">
      <table className="ts-compare-table exp-compare">
        <thead>
          <tr>
            <th className="exp-sticky-col" style={{ width: 36 }}>#</th>
            {fields.map((f) => (
              <th key={f.key} style={{ width: colWidths[f.key] }} title={f.label}>
                {f.label.length > 24 ? `${f.label.slice(0, 23)}…` : f.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((row, idx) => {
            const orig = originals[idx] ?? {};
            return (
              <tr key={idx}>
                <td className="ts-compare-day exp-sticky-col">{idx + 1}</td>
                {fields.map((f) => {
                  const v = cellVal(row[f.key]);
                  const o = idx < originals.length ? cellVal(orig[f.key]) : undefined;
                  const c = cellConf(row[f.key]);
                  const isActive = activeCell?.row === idx && activeCell?.field === f.key;
                  const isZoomed = zoomedCell?.row === idx && zoomedCell?.field === f.key;
                  const bbox = spatial
                    ? spatialCellBboxPct(spatial, idx, f.key)
                    : expenseCellBboxPct(idx, f.key);
                  const colW = colWidths[f.key] ?? 160;
                  const issue = plausIssues[`${idx}.${f.key}`];
                  return (
                    <td key={f.key}
                        className={`ts-compare-cell ${isActive ? "ts-cell-active" : ""} ${issue ? "ts-plaus-red" : ""}`}
                        style={{ width: colW }}
                        title={issue}
                        data-row={idx}
                        data-field={f.key}
                        onMouseEnter={() => onCellHover({ row: idx, field: f.key })}
                        onMouseLeave={() => onCellHover(null)}>
                      {/* Vertikal: Crop in VOLLER Inhalts-Breite oben (Spalte ist
                          nach der Crop-Geometrie dimensioniert → nichts wird
                          abgeschnitten), Wert-Eingabe darunter. */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                        {bbox && imgAspect !== null && (
                          <CellCrop
                            previewUrl={previewUrl}
                            bbox={bbox}
                            imgAspect={imgAspect}
                            maxW={560}
                            zoomed={isZoomed}
                            onToggleZoom={() => setZoomedCell(isZoomed ? null : { row: idx, field: f.key })}
                          />
                        )}
                        <div className="ts-compare-input-wrap" style={{ width: "100%" }}>
                          <input
                            type="text"
                            value={v}
                            placeholder={f.placeholder}
                            onChange={(e) => setCell(idx, f.key, e.target.value)}
                            onFocus={(e) => { e.currentTarget.select(); onCellHover({ row: idx, field: f.key }); }}
                            onBlur={() => {
                              // Zeit-/Datums-Autoformat wie Stundenliste
                              // ("8"→"08:00", "160326"→"16.03.2026").
                              if (EXPENSE_TIME_FIELDS.has(f.key)) {
                                const formatted = formatTimeInput(v);
                                if (formatted !== v) setCell(idx, f.key, formatted);
                              } else if (f.key === "datum") {
                                const formatted = formatDateInput(v, cardYear);
                                if (formatted !== v) setCell(idx, f.key, formatted);
                              }
                            }}
                            disabled={busy || !approved}
                            title={issue ?? (!approved ? "Erst Markierungen freigeben (Banner oben)" : undefined)}
                            className="ts-compare-input"
                            style={{
                              width: Math.max(56, Math.min(colW - 46, 200)),
                              ...(issue ? { border: "2px solid #dc2626", background: "#fef2f2" } : {}),
                            }}
                          />
                          {c !== null && (
                            <span className="ts-compare-conf" title={`Konfidenz ${(c * 100).toFixed(0)}%`}>
                              {(c * 100).toFixed(0)}%
                            </span>
                          )}
                          {issue && <PlausibilityBadge reason={issue} />}
                          {/* Datum-Vererbung „wie oben“ — same code wie Übersicht
                              (lib/expensePlausibility): nur leeres ODER invalides
                              Feld darf übernehmen. */}
                          {f.key === "datum" && !busy && approved
                            && canInheritDate(entries as any, idx, cardYear)
                            && (() => {
                              const inh = inheritableDate(entries as any, idx, cardYear);
                              if (!inh) return null;
                              return (
                                <button
                                  type="button"
                                  className="link"
                                  style={{ opacity: 0.55, fontSize: 11, whiteSpace: "nowrap" }}
                                  title={`Datum wie oben übernehmen (Zeile ${inh.fromRow + 1}: ${inh.value})`}
                                  onClick={() => setCell(idx, "datum", inh.value)}
                                >⤓ wie oben</button>
                              );
                            })()}
                          {o !== undefined && v !== o && (
                            <span className="ts-compare-orig" title="Original (OCR)">
                              OCR: {o || "leer"}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
